import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../../components/layout/AdminLayout";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import {
  Search,
  Package,
  Calendar,
  Truck,
  Eye,
  AlertTriangle,
  Copy,
  Loader2,
  Receipt,
} from "lucide-react";
import {
  ORDER_STATUS_KEYS,
  ORDER_STATUS_LABELS,
  extractApiErrorMessage,
  formatDateTime,
  formatMoney,
  humanizeStatus,
  matchesSearch,
  normalizeAdminOrders,
  toNumber,
} from "../../lib/safeData";
import { executeCompleteOrderSubmission } from "../../lib/orderSubmission";

const statusColors = {
  pending_payment: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabels = ORDER_STATUS_LABELS;

const statuses = ["all", ...ORDER_STATUS_KEYS];

const initialManualOrder = {
  customer_email: "",
  customer_name: "",
  total_amount: "",
  currency: "USD",
  destination: "BB",
  notes: "",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");
  const [manualOrder, setManualOrder] = useState(initialManualOrder);
  const [creatingManual, setCreatingManual] = useState(false);
  const [manualOrderLink, setManualOrderLink] = useState("");

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { limit: 100 };
      if (statusFilter !== "all") params.status = statusFilter;

      const response = await axios.get(`${API}/admin/orders`, {
        params,
        withCredentials: true,
      });
      const rows = normalizeAdminOrders(response.data);
      setOrders(rows);
      setTotal(toNumber(response.data?.total, rows.length));
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      setTotal(0);
      setLoadError(extractApiErrorMessage(error, "Failed to load orders"));
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualOrder((current) => ({ ...current, [name]: value }));
  };

  /**
   * Manual order fallback: records a pending-payment order for a customer and
   * hands back a checkout link that can be sent to them directly.
   */
  const generateManualOrderLink = async (e) => {
    e.preventDefault();
    setCreatingManual(true);
    setManualOrderLink("");

    const result = await executeCompleteOrderSubmission(
      {
        customerEmail: manualOrder.customer_email,
        customerName: manualOrder.customer_name,
        totalAmount: toNumber(manualOrder.total_amount),
        currency: manualOrder.currency,
        destination: manualOrder.destination,
        notes: manualOrder.notes,
        items: [],
      },
      { apiBase: API, origin: window.location.origin }
    );

    setCreatingManual(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }

    setManualOrderLink(result.checkoutUrl);
    setManualOrder(initialManualOrder);
    toast.success(`Manual order created for ${formatMoney(result.totalAmount, manualOrder.currency)}`);
    fetchOrders();
  };

  const copyManualOrderLink = async () => {
    try {
      await navigator.clipboard.writeText(manualOrderLink);
      toast.success("Checkout link copied");
    } catch (error) {
      console.error("Clipboard write failed:", error);
      toast.error("Copy failed — select the link and copy it manually");
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        null,
        {
          params: { status: newStatus },
          withCredentials: true,
        }
      );
      toast.success("Order status updated");
      fetchOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error(extractApiErrorMessage(error, "Failed to update order status"));
    }
  };

  const filteredOrders = orders.filter((order) =>
    matchesSearch(
      [order.order_id, order.customer_email, order.customer_name],
      search
    )
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="admin-orders-title">
            Orders
          </h1>
          <p className="text-slate-600">
            {total} total orders
          </p>
        </div>

        {loadError && (
          <div
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4"
            data-testid="admin-orders-load-error"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="font-medium text-red-700">Could not load orders</p>
              <p className="text-sm text-red-600">{loadError}</p>
            </div>
            <Button size="sm" variant="outline" onClick={fetchOrders}>
              Retry
            </Button>
          </div>
        )}

        {/* Manual order fallback — generate a payment link for a customer */}
        <form
          onSubmit={generateManualOrderLink}
          className="rounded-lg border border-slate-200 bg-white p-4"
          data-testid="manual-order-form"
        >
          <div className="mb-3 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-slate-500" />
            <h2 className="font-semibold text-slate-900">Manual order &amp; payment link</h2>
          </div>
          <p className="mb-4 text-sm text-slate-600">
            Record an order taken off-site and send the customer a checkout link. The order is
            created with status <span className="font-medium">Pending Payment</span>.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <Label htmlFor="manual-order-email">Customer Email *</Label>
              <Input
                id="manual-order-email"
                name="customer_email"
                type="email"
                value={manualOrder.customer_email}
                onChange={handleManualChange}
                placeholder="customer@example.com"
                data-testid="manual-order-email"
              />
            </div>
            <div>
              <Label htmlFor="manual-order-name">Customer Name</Label>
              <Input
                id="manual-order-name"
                name="customer_name"
                value={manualOrder.customer_name}
                onChange={handleManualChange}
                placeholder="Optional"
                data-testid="manual-order-name"
              />
            </div>
            <div>
              <Label htmlFor="manual-order-amount">Amount *</Label>
              <Input
                id="manual-order-amount"
                name="total_amount"
                type="number"
                step="0.01"
                min="0"
                value={manualOrder.total_amount}
                onChange={handleManualChange}
                placeholder="114.99"
                data-testid="manual-order-amount"
              />
            </div>
            <div>
              <Label htmlFor="manual-order-currency">Currency</Label>
              <Select
                value={manualOrder.currency}
                onValueChange={(v) => setManualOrder((c) => ({ ...c, currency: v }))}
              >
                <SelectTrigger id="manual-order-currency" data-testid="manual-order-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["USD", "BBD"].map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4">
              <Label htmlFor="manual-order-notes">Notes</Label>
              <Textarea
                id="manual-order-notes"
                name="notes"
                rows={2}
                value={manualOrder.notes}
                onChange={handleManualChange}
                placeholder="What the customer ordered, agreed shipping, etc."
                data-testid="manual-order-notes"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={creatingManual}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="manual-order-submit"
              >
                {creatingManual ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Generate Link"
                )}
              </Button>
            </div>
          </div>

          {manualOrderLink && (
            <div
              className="mt-4 flex flex-col gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 sm:flex-row sm:items-center"
              data-testid="manual-order-link"
            >
              <code className="flex-1 break-all text-xs text-emerald-900">{manualOrderLink}</code>
              <Button type="button" size="sm" variant="outline" onClick={copyManualOrderLink}>
                <Copy className="mr-2 h-3 w-3" />
                Copy
              </Button>
            </div>
          )}
        </form>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by order ID, name, or email..."
              className="pl-10"
              data-testid="search-orders-input"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status === "all" ? "All Statuses" : statusLabels[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={8}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Package className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No orders found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell>
                      <p className="mono text-sm font-medium">
                        {order.order_id}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {order.customer_name || "N/A"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {order.customer_email || "N/A"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Package className="h-4 w-4 text-slate-400" />
                        <span>{order.items.length} items</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Truck className="h-4 w-4 text-slate-400" />
                        <span>{order.destination}</span>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${
                        order.total ? "" : "text-amber-600"
                      }`}
                      title={order.total ? undefined : "No total recorded on this order"}
                    >
                      {formatMoney(order.total, order.currency)}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={order.status_key || undefined}
                        onValueChange={(value) =>
                          handleStatusUpdate(order.order_id, value)
                        }
                      >
                        <SelectTrigger
                          className={`w-[140px] h-8 ${statusColors[order.status_key] || "bg-slate-100 text-slate-600"}`}
                          data-testid={`status-select-${order.order_id}`}
                        >
                          <SelectValue placeholder={humanizeStatus(order.status)} />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses
                            .filter((s) => s !== "all")
                            .map((status) => (
                              <SelectItem key={status} value={status}>
                                {statusLabels[status]}
                              </SelectItem>
                            ))}
                          {/* Keep an unmapped backend status selectable so the
                              trigger renders its real value instead of blank. */}
                          {order.status_key && !ORDER_STATUS_KEYS.includes(order.status_key) && (
                            <SelectItem value={order.status_key}>
                              {humanizeStatus(order.status)}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Calendar className="h-3 w-3" />
                        {formatDateTime(order.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          window.open(`/orders/${order.order_id}`, "_blank")
                        }
                        data-testid={`view-${order.order_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
}
